import { useEffect, useMemo, useState } from "react";
import { ROSLibJSControlSource } from "arcjs/control_source/ROSLibJS";
import { FoxgloveControlSource } from "arcjs/control_source/Foxglove";
import { IControlSource } from "arcjs/control_source/base";
import { attachRosTopicWithRetry, LATE_ROS_TOPIC_OPTIONS } from "lib/ros/rosTopicSubscribeWithRetry";
import { ARC_GATEWAY, arcPathPrefix } from "lib/consts/arcGateway";
import { getAuthToken } from "lib/utils/auth";

// Fleet-wide control source, same env switch SingleARCProvider uses. The dashboard
// probe must match whatever the ARCs actually serve on the bridge port (rosbridge vs
// foxglove_bridge) or every ARC reads as offline.
const CONTROL_SOURCE = process.env.REACT_APP_CONTROL_SOURCE || "ROSLIBJS";

/** One path-routed probe socket per ARC, source-agnostic. */
function makeProbeSource(proxyHostname: string, sn: string): IControlSource {
    const options = {
        useSSL: true,
        token: getAuthToken() ?? undefined,
        port: null as null,
        subpath: `${arcPathPrefix(sn)}${ARC_GATEWAY.ros}`.replace(/^\//, ""),
    };
    if (CONTROL_SOURCE === "FOXGLOVE") {
        return new FoxgloveControlSource(proxyHostname, options);
    }
    return new ROSLibJSControlSource(proxyHostname, options);
}

export type RosbridgeProbeResult = {
    onlineMap: Record<string, boolean>;
    batteryMap: Record<string, number | undefined>;
};

/**
 * Probes bridge connectivity for a list of ARCs through the arc_proxy.
 *
 * Opens one path-routed control-source WebSocket per serial number
 * (wss://<proxyHostname>/arc/<sn>/api/ros),
 * choosing rosbridge or foxglove_bridge from REACT_APP_CONTROL_SOURCE. Both sources
 * reconnect automatically (with a heartbeat/liveness watchdog that force-drops a silently
 * dead socket), so onConnect/onDisconnect keep the maps current without any manual retry
 * logic.
 *
 * Returns { onlineMap, batteryMap } — both keyed by serial number.
 */
export function useBridgeProbe(
    proxyHostname: string | null,
    serialNumbers: string[],
): RosbridgeProbeResult {
    const [onlineMap, setOnlineMap] = useState<Record<string, boolean>>({});
    const [batteryMap, setBatteryMap] = useState<Record<string, number | undefined>>({});

    // Stable dep string so the effect only re-runs when the set of serials changes.
    const snKey = useMemo(() => serialNumbers.slice().sort().join(","), [serialNumbers]);

    useEffect(() => {
        if (!proxyHostname || serialNumbers.length === 0) {
            setOnlineMap({});
            setBatteryMap({});
            return;
        }

        const sources: IControlSource[] = [];
        // Battery topic cleanup per ARC — called on disconnect or unmount.
        const batteryCleanups = new Map<string, () => void>();

        for (const sn of serialNumbers) {
            const source = makeProbeSource(proxyHostname, sn);

            source.onConnect(() => {
                setOnlineMap((prev) => ({ ...prev, [sn]: true }));

                const cleanup = attachRosTopicWithRetry(
                    source,
                    "/arc/state/battery_level",
                    (data) => {
                        if (typeof data === "number" && !Number.isNaN(data)) {
                            setBatteryMap((prev) => ({ ...prev, [sn]: data }));
                        }
                    },
                    {
                        ...LATE_ROS_TOPIC_OPTIONS,
                        markMessageReceived: (d) => typeof d === "number" && !Number.isNaN(d),
                    },
                );
                batteryCleanups.set(sn, cleanup);
            });

            source.onDisconnect(() => {
                setOnlineMap((prev) => ({ ...prev, [sn]: false }));
                setBatteryMap((prev) => ({ ...prev, [sn]: undefined }));
                batteryCleanups.get(sn)?.();
                batteryCleanups.delete(sn);
            });

            source.onError(() =>
                setOnlineMap((prev) => ({ ...prev, [sn]: false }))
            );

            sources.push(source);
        }

        return () => {
            for (const cleanup of batteryCleanups.values()) cleanup();
            for (const source of sources) {
                try { source.close(); } catch { }
            }
        };
        // snKey is the stable serialisation of serialNumbers — intentional.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proxyHostname, snKey]);

    return { onlineMap, batteryMap };
}
